/**
 * BenchmarkPage (`/evals/benchmarks/:slug`) — the failure-analysis eval
 * view (TRA-831, Mercor console inspired): open stats header (nothing
 * truncates), segmented model filter (?model=), score distribution,
 * and the task ledger. The step-grading matrix appears only for
 * benchmarks whose export carries step rewards — no lone tab strip
 * otherwise.
 */

import { useState } from "react";
import { Navigate, useParams, useSearchParams } from "react-router-dom";
import { getApiErrorDetail, getApiErrorStatus } from "@/api/client";

import { selectedModel } from "@/components/evals/analysis";
import {
  AnalysisLedger,
  type LedgerTaskDetails,
} from "@/components/evals/AnalysisLedger";
import { EvalBreadcrumb } from "@/components/evals/EvalBreadcrumb";
import { TaskScoreDistribution } from "@/components/evals/TaskScoreDistribution";
import {
  attributionBreakdown,
  benchmarkGridCsv,
  countDifficulties,
  loadSeriesSelection,
  taskSlugsForLeaf,
  passMetricLabel,
  passScoreFns,
  saveSeriesSelection,
  sectionScores,
  SERIES_LABELS,
  toAnalysisTasks,
  type PassMetric,
  type ScoreSeries,
} from "@/components/benchmarks/benchmarkAnalysis";
import { AnalysisFilter } from "@/components/benchmarks/AnalysisFilter";
import { BenchmarkStatsHeader } from "@/components/benchmarks/BenchmarkStatsHeader";
import { TaskSectionBlocks } from "@/components/benchmarks/TaskSectionBlocks";
import { FailureBreakdown } from "@/components/benchmarks/FailureBreakdown";
import { StepGradingMatrix } from "@/components/benchmarks/StepGradingMatrix";
import { Button } from "@/components/ui/button";
import { PageContainer } from "@/components/ui/page-container";
import { PageHeader } from "@/components/ui/page-header";
import { downloadTextFile } from "@/lib/download";
import { LoadingPanel } from "@/components/shared/LoadingScreen";
import { useMinimumLoading } from "@/hooks/useMinimumSplash";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useBenchmark } from "@/hooks/useBenchmarks";
import type {
  BenchmarkDetailResponse,
  BenchmarkScore,
} from "@/api/generated/model";

/**
 * Per-series score lookups: (task, model) when a model is selected,
 * aggregated across models otherwise (max for best, min for worst,
 * mean-of-model-means for average — matching the headline stat).
 */
function seriesScoreFns(
  scores: BenchmarkScore[],
): Record<
  ScoreSeries,
  (taskSlug: string, model: string | null) => number | null
> {
  const perModel = new Map<string, number>();
  const best = new Map<string, number>();
  const worst = new Map<string, number>();
  const means = new Map<string, number[]>();
  for (const score of scores) {
    perModel.set(`${score.task_slug}|${score.model}|best`, score.best_score);
    perModel.set(`${score.task_slug}|${score.model}|mean`, score.mean_score);
    perModel.set(`${score.task_slug}|${score.model}|worst`, score.worst_score);
    best.set(
      score.task_slug,
      Math.max(best.get(score.task_slug) ?? 0, score.best_score),
    );
    worst.set(
      score.task_slug,
      Math.min(worst.get(score.task_slug) ?? 1, score.worst_score),
    );
    const list = means.get(score.task_slug) ?? [];
    list.push(score.mean_score);
    means.set(score.task_slug, list);
  }
  const aggregate: Record<
    ScoreSeries,
    (taskSlug: string) => number | undefined
  > = {
    best: (taskSlug) => best.get(taskSlug),
    worst: (taskSlug) => worst.get(taskSlug),
    mean: (taskSlug) => {
      const list = means.get(taskSlug);
      return list === undefined
        ? undefined
        : list.reduce((sum, v) => sum + v, 0) / list.length;
    },
  };
  const lookup =
    (key: ScoreSeries) =>
    (taskSlug: string, model: string | null): number | null =>
      (model === null
        ? aggregate[key](taskSlug)
        : perModel.get(`${taskSlug}|${model}|${key}`)) ?? null;
  return { best: lookup("best"), mean: lookup("mean"), worst: lookup("worst") };
}

function buildDetails(
  detail: BenchmarkDetailResponse,
): Map<string, LedgerTaskDetails> {
  const stepsByTask = new Map<string, number>();
  for (const score of detail.scores) {
    const current = stepsByTask.get(score.task_slug) ?? 0;
    stepsByTask.set(
      score.task_slug,
      Math.max(current, score.step_rewards.length),
    );
  }
  return new Map(
    detail.tasks.map((task) => [
      task.slug,
      {
        section: task.section,
        world: task.world,
        outputType: task.output_type,
        requiredSkills: task.required_skills,
        steps: stepsByTask.get(task.slug) ?? 0,
        fairness: task.fairness,
        modelFailure: task.model_failure,
        modelFailureDetail: task.model_failure_detail,
        failedStep: task.failed_step,
        verifierStatus: task.verifier_status,
        verifierIssue: task.verifier_issue,
        verifierIssueDetail: task.verifier_issue_detail,
        trainingReadiness: task.training_readiness,
        goldResponse: task.gold_response,
        goldResponseType: task.gold_response_type,
      },
    ]),
  );
}

export default function BenchmarkPage(): React.JSX.Element {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const model = selectedModel(searchParams);
  const {
    data: detail,
    isLoading: isFetching,
    isError,
    error,
  } = useBenchmark(slug);
  // Hold the loading panel long enough for the animated icon to be seen.
  const isLoading = useMinimumLoading(isFetching);

  // Which score statistics the chart plots — persisted per benchmark so
  // the eval card on the list page shows the same selection. Lazy init
  // is enough: navigating between benchmarks always remounts this page.
  const [seriesSelection, setSeriesSelection] = useState<ScoreSeries[]>(() =>
    slug !== undefined ? loadSeriesSelection(slug) : ["best"],
  );
  const selectSeries = (next: ScoreSeries[]): void => {
    setSeriesSelection(next);
    if (slug !== undefined) saveSeriesSelection(slug, next);
  };
  // pass@k / pass^k overlay — session-only state; the score-series
  // selection above stays the persisted "primary" read.
  const [passMetric, setPassMetric] = useState<PassMetric | null>(null);
  const [passK, setPassK] = useState(4);
  // Picking a model defaults the statistic to Average (per-model average
  // is the natural read); All models defaults back to Highest.
  const selectModelFilter = (nextModel: string | null): void => {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (nextModel === null) {
          next.delete("model");
        } else {
          next.set("model", nextModel);
        }
        // A failure-type selection belongs to the old scope: the new model
        // may have no runs of that leaf, stranding an empty ledger with no
        // row left to click, so the filter resets with the scope.
        next.delete("failure");
        return next;
      },
      { replace: true },
    );
    selectSeries(nextModel === null ? ["best"] : ["mean"]);
  };

  const analysisTasks = detail
    ? toAnalysisTasks(detail.tasks, detail.scores)
    : [];
  const hasStepData = detail
    ? detail.scores.some((s) => s.step_rewards.length > 0)
    : false;
  const taskPath = (taskSlug: string): string =>
    `/evals/benchmarks/${slug ?? ""}/tasks/${taskSlug}`;

  const scored = analysisTasks.filter((task) => task.score !== null);
  const meanScore =
    scored.length > 0
      ? scored.reduce((sum, task) => sum + (task.score ?? 0), 0) / scored.length
      : null;

  const taskDetails = detail ? buildDetails(detail) : undefined;

  const scoreFns = seriesScoreFns(detail?.scores ?? []);
  const bestFor = scoreFns.best;
  const passFns = passScoreFns(detail?.scores ?? [], passK);
  // A pass metric REPLACES the score series — both answer "which
  // y-value per task", so the stat bar is one radio across all six.
  const distributionSeries =
    passMetric === null
      ? seriesSelection.map((key) => ({
          key,
          label: SERIES_LABELS[key],
          scoreFor: scoreFns[key],
        }))
      : [
          {
            key: passMetric,
            label: passMetricLabel(passMetric, passK),
            scoreFor: passFns[passMetric],
          },
        ];
  const subtitleLabel = distributionSeries
    .map((series) => series.label)
    .join(" · ");

  // Click-through from the failure Pareto: ?failure=<leaf> narrows the
  // ledger to tasks with at least one model-failure run of that type.
  const failureLeaf = searchParams.get("failure");
  const selectFailureLeaf = (leaf: string | null): void => {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (leaf === null) {
          next.delete("failure");
        } else {
          next.set("failure", leaf);
        }
        return next;
      },
      { replace: true },
    );
  };
  const failureSlugs =
    detail && failureLeaf !== null
      ? taskSlugsForLeaf(detail.attributions, model, failureLeaf)
      : null;
  const ledgerTasks =
    failureSlugs === null
      ? analysisTasks
      : analysisTasks.filter((task) => failureSlugs.has(task.slug));
  const breakdown = attributionBreakdown(detail?.attributions ?? [], model);
  // The active failure filter's leaf row (model-failure or non-model). A
  // stale URL can name a leaf outside the current scope; the raw id stands
  // in as the label then and the run count is unknown.
  const failureRow = [
    ...breakdown.families.flatMap((family) => family.leaves),
    ...breakdown.nonModelIssues,
  ].find((leaf) => leaf.leaf === failureLeaf);
  const failureLabel = failureRow?.label ?? failureLeaf;
  // Labels are sentence-cased for the panel rows; mid-sentence they read
  // as a stray capital, so the caption lowercases the first letter.
  const failureLabelMidSentence =
    failureLabel === null
      ? ""
      : failureLabel.charAt(0).toLowerCase() + failureLabel.slice(1);

  const analysisBody = detail && (
    <>
      <TaskSectionBlocks
        tasks={ledgerTasks}
        sections={new Map(detail.tasks.map((task) => [task.slug, task.section]))}
        model={model}
        taskPath={taskPath}
      />
      <TaskScoreDistribution
        tasks={analysisTasks}
        model={model}
        taskPath={taskPath}
        details={taskDetails}
        series={distributionSeries}
        subtitle={`${subtitleLabel} score per task${model === null ? " across models" : ""}`}
      />
      <FailureBreakdown
        breakdown={breakdown}
        selectedLeaf={failureLeaf}
        onSelectLeaf={selectFailureLeaf}
      />
      <AnalysisLedger
        tasks={ledgerTasks}
        model={model}
        taskPath={taskPath}
        details={taskDetails}
        bestFor={bestFor}
        toolbar={
          failureLeaf !== null ? (
            <p className="text-sm text-muted-foreground">
              {failureRow
                ? `${ledgerTasks.length.toString()} of ${analysisTasks.length.toString()} tasks contain ${failureRow.count.toString()} ${failureLabelMidSentence} runs.`
                : `Showing tasks with ${failureLabel ?? ""} failures: ${ledgerTasks.length.toString()} of ${analysisTasks.length.toString()} tasks`}{" "}
              <button
                type="button"
                className="ml-1 underline underline-offset-4 hover:text-foreground"
                onClick={() => {
                  selectFailureLeaf(null);
                }}
              >
                Clear filter
              </button>
            </p>
          ) : undefined
        }
        empty={
          failureSlugs !== null
            ? {
                title: "No tasks match this failure filter",
                description:
                  "No task in this scope failed this way. Clear the filter in the Failure types panel.",
              }
            : undefined
        }
      />
    </>
  );

  return (
    <PageContainer className="space-y-6">
      <EvalBreadcrumb
        crumbs={[
          { label: "Evals", to: "/evals" },
          { label: detail?.manifest.display_name ?? slug ?? "" },
        ]}
      />

      {isLoading && <LoadingPanel />}

      {isError && getApiErrorStatus(error) === 404 && (
        <Navigate to="/evals" replace />
      )}
      {isError && getApiErrorStatus(error) !== 404 && (
        <p className="text-sm text-destructive">
          Failed to load benchmark: {getApiErrorDetail(error) ?? "unknown error"}
        </p>
      )}

      {!isLoading && !isError && detail && (
        <>
          <PageHeader
            title={detail.manifest.display_name}
            description={
              detail.manifest.description !== ""
                ? detail.manifest.description
                : "Benchmark analysis streamed from the team data bucket."
            }
            actions={
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => {
                  downloadTextFile(
                    benchmarkGridCsv(
                      detail.manifest,
                      detail.tasks,
                      detail.scores,
                      detail.attributions,
                    ),
                    `${slug ?? "benchmark"}-grid.csv`,
                    "text/csv",
                  );
                }}
              >
                Export CSV
              </Button>
            }
          />

          <BenchmarkStatsHeader
            taskCount={detail.manifest.task_count}
            meanScore={meanScore}
            models={detail.manifest.models}
            sections={sectionScores(detail.tasks, analysisTasks)}
            difficulty={countDifficulties(analysisTasks)}
          />

          <AnalysisFilter
            models={detail.manifest.models}
            model={model}
            onModel={selectModelFilter}
            series={seriesSelection}
            onSeries={selectSeries}
            passMetric={passMetric}
            onPassMetric={setPassMetric}
            passK={passK}
            onPassK={setPassK}
          />

          {hasStepData ? (
            <Tabs defaultValue="tasks">
              <TabsList>
                <TabsTrigger value="tasks">Task Index</TabsTrigger>
                <TabsTrigger value="steps">Step grading</TabsTrigger>
              </TabsList>
              <TabsContent value="tasks" className="space-y-6">
                {analysisBody}
              </TabsContent>
              <TabsContent value="steps">
                <StepGradingMatrix scores={detail.scores} taskPath={taskPath} />
              </TabsContent>
            </Tabs>
          ) : (
            <div className="space-y-6">{analysisBody}</div>
          )}
        </>
      )}
    </PageContainer>
  );
}
